import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { DraftPrice } from '@/design-system/billing';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import { useErrorToast } from '@/core/errors/useErrorToast';
import { plansService, type Plan } from '../api/plans.service';
import type { PlanFormState } from '../types';
import { buildPlanLimits, generatePlanSlug } from '../utils/plan-build';
import { planLicensingService, type PlanTier } from '../api/plan-licensing.service';

type LicensingDraft = { packageId?: string; expectedPackageVersion?: number; tier: PlanTier; capabilityKeys: string[]; expectedVersion: number; isCustom?: boolean; isPublic?: boolean };

function invalidatePlansAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: queryKeys.plans.all });
  void qc.invalidateQueries({ queryKey: queryKeys.companies.all });
}

export function usePlanMutations() {
  const qc = useQueryClient();
  const onError = useErrorToast();

  const createPlan = useMutation({
    mutationFn: async (input: { form: PlanFormState; priceDrafts: DraftPrice[]; licensing: LicensingDraft; isActive: boolean }) => {
      const defaultDraft = input.priceDrafts.find((d) => d.isDefault) ?? input.priceDrafts[0];
      const created = await plansService.create({
        isActive: input.isActive,
        name: input.form.name,
        slug: generatePlanSlug(input.form.name),
        description: input.form.description || undefined,
        price: defaultDraft.unitAmount,
        currency: defaultDraft.currency,
        interval: input.form.interval,
        limits: buildPlanLimits(input.form),
        licensing: { tier: input.licensing.tier, capabilityKeys: input.licensing.capabilityKeys, isCustom: input.licensing.isCustom, isPublic: input.licensing.isPublic, packageId: input.licensing.packageId, expectedPackageVersion: input.licensing.expectedPackageVersion },
      });
      const extraDrafts = input.priceDrafts.filter((d) => d.currency !== defaultDraft.currency);
      for (const draft of extraDrafts) {
        await plansService.upsertPrice(created.id, {
          currency: draft.currency,
          unitAmount: draft.unitAmount,
          isDefault: false,
        });
      }
      return created;
    },
    onSuccess: () => {
      invalidatePlansAll(qc);
      toast.success('Plano criado com sucesso!');
    },
    onError: (e) => onError(e, 'Erro ao criar plano'),
  });

  const updatePlan = useMutation({
    mutationFn: async (input: { planId: string; form: PlanFormState; licensing: LicensingDraft; isActive: boolean }) => {
      return planLicensingService.configure(input.planId, { ...input.licensing, details: {
        isActive: input.isActive,
        name: input.form.name,
        slug: generatePlanSlug(input.form.name),
        description: input.form.description || undefined,
        interval: input.form.interval,
        limits: buildPlanLimits(input.form) ?? {},
      } });
    },
    onSuccess: () => {
      invalidatePlansAll(qc);
      toast.success('Plano atualizado com sucesso!');
    },
    onError: (e) => onError(e, 'Erro ao atualizar plano'),
  });

  const deletePlan = useMutation({
    mutationFn: (planId: string) => plansService.delete(planId),
    onSuccess: () => {
      invalidatePlansAll(qc);
      toast.success('Plano deletado com sucesso!');
    },
    onError: (e) => onError(e, 'Erro ao deletar plano'),
  });

  const toggleActive = useMutation({
    mutationFn: (input: { plan: Plan; nextActive: boolean }) =>
      plansService.toggleActive(input.plan.id, input.nextActive),
    onSuccess: (_data, variables) => {
      invalidatePlansAll(qc);
      toast.success(
        variables.nextActive ? 'Plano ativado com sucesso!' : 'Plano desativado com sucesso!'
      );
    },
    onError: (e) => onError(e, 'Erro ao alterar status'),
  });

  const syncStripe = useMutation({
    mutationFn: (plan: Plan) => plansService.syncStripe(plan.id),
    onSuccess: (_data, plan) => {
      toast.success(
        `Sincronização com Stripe enfileirada para "${plan.name}". Atualizando em instantes...`
      );
      setTimeout(() => {
        invalidatePlansAll(qc);
      }, 4000);
    },
    onError: (e) => onError(e, 'Erro ao sincronizar com Stripe'),
  });

  return {
    createPlan,
    updatePlan,
    deletePlan,
    toggleActive,
    syncStripe,
  };
}

export type PlanEditorMutations = Pick<
  ReturnType<typeof usePlanMutations>,
  'createPlan' | 'updatePlan' | 'toggleActive'
>;
